import { UseQueryResult } from 'react-query';
import { useMyQuery, QueryInfos, Options } from '../helpers';
import {
    ApiType,
    ExportContractGroupUsersInput,
    ExportContractUsersInput,
    GetContractInfosInput,
    GetContractTextInput,
    GetGroupDetailInput,
    GetGroupsInput,
    SearchGroupCollabsInput,
} from '../../apiTypes';
import { typeContract } from './types';
import { STALETIME_AUTOCOMPLETE } from '../../../utils/constants';

const contractInfoQuery = (variables: GetGroupsInput): QueryInfos => {
    const { id: contractId } = variables;
    return {
        name: `contract${contractId}`,
        path: `/api/contract/${contractId}`,
        apiType: ApiType.ID,
        type: typeContract.GET_CONTRACT_INFO,
        params: {
            headcount: 1,
            invitationcount: 1,
        },
    };
};

const groupsQuery = (variables: GetGroupsInput): QueryInfos => {
    const { id: contractId } = variables;
    return {
        name: `groups${contractId}`,
        path: `/api/contract/${contractId}/groups`,
        apiType: ApiType.ID,
        type: typeContract.GET_GROUPS,
        params: {},
    };
};

const groupDetailsQuery = (variables: GetGroupDetailInput): QueryInfos => {
    const { id: contractId, groupId } = variables;
    return {
        name: `collab${contractId}${groupId}`,
        path: `/api/contract/${contractId}/group/${groupId}`,
        apiType: ApiType.ID,
        type: typeContract.GET_GROUP_DETAILS,
        params: {},
    };
};

const collabsQuery = (variables: GetGroupsInput): QueryInfos => {
    const { id: contractId } = variables;
    return {
        name: `collabs${contractId}`,
        path: `/api/contract/${contractId}/coworkers`,
        apiType: ApiType.ID,
        type: typeContract.GET_COLLABS,
        params: {},
    };
};

const searchCollabsQuery = (variables: SearchGroupCollabsInput): QueryInfos => {
    const { id: contractId, ...rest } = variables;
    return {
        name: `searchCollabs${contractId}`,
        path: `/api/contract/${contractId}/search`,
        apiType: ApiType.ID,
        type: typeContract.SEARCH_COLLABS,
        params: rest,
    };
};

const searchCollabsNoCacheQuery = (variables: SearchGroupCollabsInput): QueryInfos => {
    const { id: contractId, ...rest } = variables;
    return {
        name: `searchCollabsNoCacheQuery${contractId}`,
        path: `/api/contract/${contractId}/no-cache-search`,
        apiType: ApiType.ID,
        type: typeContract.SEARCH_COLLABS_NO_CACHE,
        params: rest,
    };
};

const exportContractUsers = (variables: ExportContractUsersInput): QueryInfos => {
    const { contractId } = variables;
    return {
        name: `exportContractUsers${contractId}`,
        path: `/api/contract/${contractId}/export/csv/users`,
        apiType: ApiType.ID,
        type: typeContract.EXPORT_CONTRACT_USERS,
        params: {},
        responseType: 'blob', // To get blob as response type for download
    };
};

const exportContractGroupUsers = (variables: ExportContractGroupUsersInput): QueryInfos => {
    const { contractId, groupId } = variables;
    return {
        name: `exportContractGroupUsers${contractId}`,
        path: `/api/contract/${contractId}/group/${groupId}/export/csv/users`,
        apiType: ApiType.ID,
        type: typeContract.EXPORT_CONTRACT_GROUP_USERS,
        params: {},
        responseType: 'blob', // To get blob as response type for download
    };
};

const hasUnsignedContract = (): QueryInfos => {
    return {
        name: `contractUnsigned`,
        path: `/api/contract/unsigned`,
        apiType: ApiType.ID,
        type: typeContract.HAS_UNSIGNED_CONTRACT,
        params: {},
    };
};
const getContractText = (variables: GetContractTextInput): QueryInfos => {
    const { contractId } = variables;
    return {
        name: `contractWording${contractId}`,
        path: `/api/contract/${contractId}/generate/wording`,
        apiType: ApiType.ID,
        type: typeContract.GET_CONTRACT_TEXT,
        params: {},
    };
};

const rolesQuery = (): QueryInfos => {
    return {
        name: `roles`,
        path: `/api/roles`,
        apiType: ApiType.ID,
        type: typeContract.GET_ROLES,
        params: {},
    };
};

//= ==== Commons =====//

export function useGetContractInfos<T>(options?: Options<GetContractInfosInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetContractInfosInput>(contractInfoQuery, {
        ...options,
    });
}

export function useGetGroups<T>(options?: Options<GetGroupsInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetGroupsInput>(groupsQuery, {
        staleTime: STALETIME_AUTOCOMPLETE,
        ...options,
    });
}

export function useGetGroupDetails<T>(options?: Options<GetGroupDetailInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetGroupDetailInput>(groupDetailsQuery, {
        ...options,
    });
}

export function useGetCollabs<T>(options?: Options<GetGroupsInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetGroupsInput>(collabsQuery, {
        staleTime: STALETIME_AUTOCOMPLETE,
        ...options,
    });
}

//= ==== Search =====//

export function useSearchCollabs<T>(options?: Options<SearchGroupCollabsInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, SearchGroupCollabsInput>(searchCollabsQuery, {
        staleTime: STALETIME_AUTOCOMPLETE,
        ...options,
        select: (data) => data.data,
    });
}

export function useSearchCollabsNoCache<T>(options?: Options<SearchGroupCollabsInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, SearchGroupCollabsInput>(searchCollabsNoCacheQuery, {
        staleTime: 0,
        ...options,
    });
}

//= ==== Export =====//

export function useExportContractUsers<T>(options?: Options<ExportContractUsersInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, ExportContractUsersInput>(exportContractUsers, {
        staleTime: STALETIME_AUTOCOMPLETE,
        ...options,
    });
}

export function useExportContractGroupUsers<T>(
    options?: Options<ExportContractGroupUsersInput>,
): UseQueryResult<T, Error> {
    return useMyQuery<T, ExportContractGroupUsersInput>(exportContractGroupUsers, {
        staleTime: STALETIME_AUTOCOMPLETE,
        ...options,
    });
}

//= ==== Self Service =====//
export function useHasUnsignedContract<T>(options?: Options<never>): UseQueryResult<T, Error> {
    return useMyQuery<T, never>(hasUnsignedContract, {
        staleTime: STALETIME_AUTOCOMPLETE,
        retry: 0,
        ...options,
    });
}

export function useGetContractText<T>(options?: Options<GetContractTextInput>): UseQueryResult<T, Error> {
    return useMyQuery<T, GetContractTextInput>(getContractText, {
        staleTime: STALETIME_AUTOCOMPLETE,
        ...options,
    });
}

//= ==== Roles =====//

export function useGetRoles<T>(options?: Options<never>): UseQueryResult<T, Error> {
    return useMyQuery<T, never>(rolesQuery, {
        staleTime: STALETIME_AUTOCOMPLETE,
        ...options,
    });
}
